import React from 'react';
import { Form, Button, ListGroup } from 'react-bootstrap';
import DimensionsService from '../services/DimensionsService';
import EditUserRole from './EditUserRole';
import AccountService from "services/AccountService"
import { AccountRoles } from './AccountRoles';

const PermissionBlock: React.FC<{ dimension: string; account: string; onPermissionsChange: () => void }> = ({ dimension, account, onPermissionsChange }) => {
    const [userEmail, setUserEmail] = React.useState('');
    const [userRole, setUserRole] = React.useState<AccountRoles | ''>('');
    const [userList, setUserList] = React.useState<{id: string, email: string; role: AccountRoles }[]>([]);
    const [addUserInProgress, setAddUserInProgress] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    
    const updateUserList = async (usersPermission : any) => {
        try {
            const accounts = Object.keys(usersPermission);
            if (accounts.length === 0) {
                setUserList([]);
                setErrorMessage('');
                return; 
            }

            const accountsEmail =  await AccountService.getAccountsEmails(accounts);
            const formattedUserList = Object.keys(usersPermission).map((id) => ({
                id,
                email: accountsEmail[id],
                role: usersPermission[id]
            }));
            
            setUserList(formattedUserList);
            setErrorMessage('');
            
            const currentUser = formattedUserList.find(user => user.id === account);
            if (!currentUser || currentUser.role !== AccountRoles.Admin) {
                onPermissionsChange(); 
            }
        } catch (error) {
            setErrorMessage(`Failed to fetch users: ${error}`);
        }
    };

    React.useEffect(() => {
        DimensionsService.getUserPermissions(dimension)
            .then((permissions) => updateUserList(permissions))
            .catch((error) => setErrorMessage(error));
    }, [dimension]);

    const addUserHandler = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!userEmail || !userRole) {
            setErrorMessage('Please fill out both fields');
            return;
        }
        const existingUser = userList.find((user) => user.email === userEmail);
        if (existingUser) {
            if (!window.confirm(`Are you sure you want to change the role for the user with email ${userEmail}?`)) {
                return;
            }
        }
        setAddUserInProgress(true);
        try {
            const account = await AccountService.getAccountByEmail(userEmail);
            const permissions = await DimensionsService.setUserPermissions(dimension, account.id, userRole);
            await updateUserList(permissions);
            setUserEmail('');
            setUserRole('');
        } catch (error) {
            setErrorMessage(`Failed to add user: ${error}`);
        } finally {
            setAddUserInProgress(false);
        }
    };

    return (
        <div>
            <div className="d-flex align-items-center">
                
                    <Form.Control
                        type="email"
                        placeholder="Enter email"
                        required
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                    />
                
                
                    <Form.Control
                        as="select"
                        value={userRole}
                        onChange={(e) => setUserRole(e.target.value as AccountRoles)}>
                        <option value="" disabled>
                            Select role
                        </option>
                        {Object.values(AccountRoles).map((role) => (
                            <option key={role} value={role}>{role}</option>
                        ))}
                    </Form.Control>
                
                <Button
                    style={{ width: '100px' }}
                    variant="primary"
                    onClick={addUserHandler}
                    disabled={addUserInProgress}
                >
                    {addUserInProgress ? 'Adding...' : 'Add'}
                </Button>
            </div>
            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}

            <ListGroup className="mt-3">
                {userList.map((user, index) => (
                    <ListGroup.Item key={index} className="d-flex align-items-center">
                        <EditUserRole
                            dimension={dimension}
                            account={user.id}
                            email={user.email}
                            userRole={user.role}
                            onUserPermissionsUpdate={updateUserList}
                            isOnlyOneAdmin={user.role === AccountRoles.Admin 
                                        && user.email !== undefined
                                        && userList.filter(u => u.email && u.role === AccountRoles.Admin).length === 1
                            }
                        />
                    </ListGroup.Item>
                ))}
            </ListGroup>
        </div>
    );
};

export default PermissionBlock;
